/**
 * StoreSelect - 门店选择器公共组件
 *
 * 功能：
 * - 支持 store code 和 store name 同时模糊搜索（OR 关系）
 * - 下拉项展示 "storeCode - storeName"
 * - 组件值(value)为 storeCode，传给后端的也是 storeCode
 * - 内置 300ms 防抖，避免频繁请求
 * - 支持受控和非受控模式
 *
 * 使用示例：
 * <Form.Item name="storeCode" label="Store">
 *   <StoreSelect placeholder="Search by code or name" />
 * </Form.Item>
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Select, Spin } from 'antd';
import type { SelectProps } from 'antd';
import * as storeService from '../../services/master-data/store/storeService';
import type { Store } from '../../types/master-data/store';

export interface StoreSelectProps extends Omit<SelectProps<string>, 'options' | 'onSearch' | 'filterOption' | 'labelRender'> {
  /** 额外的筛选条件（如 status: 'ACTIVE'），每次请求会附带 */
  extraParams?: Partial<Store>;
  /** 防抖延时(ms)，默认 300 */
  debounceMs?: number;
  /** 自定义 label 渲染，默认 "storeCode - englishName || storeName" */
  formatLabel?: (store: Store) => string;
}

const StoreSelect: React.FC<StoreSelectProps> = ({
  extraParams,
  debounceMs = 300,
  formatLabel,
  placeholder = 'Search store code / name',
  ...restProps
}) => {
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 默认 label 格式
  const getLabel = useCallback(
    (store: Store) => {
      if (formatLabel) return formatLabel(store);
      const name = store.englishName || store.storeName || '';
      return `${store.storeCode} - ${name}`;
    },
    [formatLabel]
  );

  // 请求门店列表
  const fetchStores = useCallback(
    async (keyword?: string) => {
      setLoading(true);
      try {
        // 使用 pageStores 分页查询，以支持 code/name 模糊搜索
        const params: Record<string, any> = {
          currentPage: 1,
          pageSize: 50,
          ...extraParams,
        };

        if (keyword && keyword.trim()) {
          // 同时传 storeCode 和 storeName 进行 OR 模糊匹配
          // 后端 multi-query 接口支持 keyword 统一搜索
          params.keyword = keyword.trim();
        }

        const resp = await storeService.pageStores(params as any);
        if (resp.success && resp.data?.tableData) {
          const items = resp.data.tableData.map((store) => ({
            label: getLabel(store),
            value: store.storeCode,
          }));
          setOptions(items);
        } else {
          setOptions([]);
        }
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [extraParams, getLabel]
  );

  // 初始加载
  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // 搜索处理（带防抖）
  const handleSearch = useCallback(
    (value: string) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        fetchStores(value);
      }, debounceMs);
    },
    [fetchStores, debounceMs]
  );

  // 清理 timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <Select
      showSearch
      allowClear
      placeholder={placeholder}
      filterOption={false}
      onSearch={handleSearch}
      notFoundContent={loading ? <Spin size="small" /> : undefined}
      loading={loading}
      options={options}
      {...restProps}
    />
  );
};

export default StoreSelect;
